import { bootstrapApplication } from '@angular/platform-browser';
import {
  PreloadAllModules,
  RouteReuseStrategy,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';

import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';

import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { importProvidersFrom } from '@angular/core';
import { brokerageHeaderInterceptor } from './app/core/interceptors/brokerage-header.interceptor';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './app/core/authentication/msal-config';
import { AppInitialisationService } from './app/shared/services/app-initialisation.service';
import { LocalStorageService } from './app/shared/services/local-storage/local-storage.service';
import { ActionLogStore } from './app/shared/stores/action-log/action-log.store';
import { ApplicantContextStore } from './app/shared/stores/applicant-context/applicant-context.store';
import { ApplicationsStore } from './app/shared/stores/applications/applications.store';
import { AuthStore } from './app/shared/stores/auth/auth.store';
import { ClientStore } from './app/shared/stores/client/client.store';
import { DocumentStore } from './app/shared/stores/document/document.store';

const msalInstance = MSALInstanceFactory();

/**
 * Runtime viewport inset adaptation.
 * Android WebView often reports 0 for env(safe-area-inset-bottom). We measure the
 * visual viewport delta to approximate the gesture/navigation bar height and store
 * it in --gesture-bottom-inset. CSS uses max(env, gesture) for effective spacing.
 */
function setupViewportInsets(): void {
  const root = document.documentElement;
  const apply = () => {
    const vv = window.visualViewport;
    if (!vv) return;

    const bottomInset = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));
    root.style.setProperty('--gesture-bottom-inset', `${bottomInset}px`);
  };

  apply();
  window.visualViewport?.addEventListener('resize', apply);
  window.visualViewport?.addEventListener('scroll', apply);
  window.addEventListener('orientationchange', () => setTimeout(apply, 150));
  window.addEventListener('resize', () => apply());
}

async function main() {
  setupViewportInsets();
  await msalInstance.initialize();

  const app = await bootstrapApplication(AppComponent, {
    providers: [
      provideIonicAngular({ focusManagerPriority: ['heading', 'content'] }),
      { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
      provideRouter(routes, withPreloading(PreloadAllModules), withComponentInputBinding()),
      importProvidersFrom(MsalModule),
      { provide: MSAL_INSTANCE, useValue: msalInstance },
      { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
      },
      { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
      provideHttpClient(withFetch(), withInterceptors([brokerageHeaderInterceptor]), withInterceptorsFromDi()),
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      AuthStore,
      ApplicationsStore,
      ApplicantContextStore,
      ClientStore,
      DocumentStore,
      ActionLogStore,
      LocalStorageService,
      AppInitialisationService,
      provideNativeDateAdapter(),
    ],
  });

  const appInitService = app.injector.get(AppInitialisationService);
  await appInitService.initialise();

  if (Capacitor.isNativePlatform()) {
    // Configure the status bar for native platforms
    try {
      document.documentElement.classList.add('native');
      if (Capacitor.getPlatform() === 'android') {
        document.documentElement.classList.add('android');
      }

      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    } catch (e) {
      console.warn('StatusBar configuration skipped', e);
    }
  }
}

main().catch((err) => console.error(err));
